import type { LucideIcon } from 'lucide-react';
import { Cog, FlaskConical, Package, Cpu, Utensils, Car, Building2, Droplets } from 'lucide-react';

export interface Industry {
  slug: string;
  name: string;
  icon: LucideIcon;
  challenge: string;
  solution: string;
  modules: string[];
}

export const INDUSTRIES: Industry[] = [
  {
    slug: 'machining',
    name: 'CNC & Machining',
    icon: Cog,
    challenge: 'Mixed-vintage CNCs, spindle utilization nobody measures, and downtime explained after the fact.',
    solution: 'MTConnect and FOCAS straight to the control, OEE and downtime Pareto per machine, and andon scoreboards on the shop floor.',
    modules: ['connectivity', 'analytics', 'mes'],
  },
  {
    slug: 'plastics',
    name: 'Plastics & Packaging',
    icon: Package,
    challenge: 'Process drift on presses and lines, recipe changes done by hand, scrap found at the end of the run.',
    solution: 'SPC on critical process values, versioned recipes applied in one step and read back to confirm, and alarm pipelines that call the shift lead.',
    modules: ['analytics', 'mes', 'alarms'],
  },
  {
    slug: 'food',
    name: 'Food & Beverage',
    icon: Utensils,
    challenge: 'Temperature and batch records kept on paper, audits that take days to prepare.',
    solution: 'Historian-backed batch records, forms for manual checks, scheduled PDF and CSV reports and a long-term cloud archive.',
    modules: ['analytics', 'mes', 'security'],
  },
  {
    slug: 'automotive',
    name: 'Automotive & Assembly',
    icon: Car,
    challenge: 'Robots, vision stations and PLCs from different vendors, with traceability spread across them.',
    solution: 'Robot and vision drivers beside the PLCs, vision-guided pick points in the robot’s own coordinates, barcode reads as tags, and work orders tracked against target at the line.',
    modules: ['connectivity', 'vision', 'mes'],
  },
  {
    slug: 'electronics',
    name: 'Electronics & Semiconductor',
    icon: Cpu,
    challenge: 'SECS/GEM tools, inspection data and yield metrics that live in separate systems.',
    solution: 'SECS/GEM connectivity, printed-text and DataMatrix verification, and SPC on the measurements that matter.',
    modules: ['connectivity', 'vision', 'analytics'],
  },
  {
    slug: 'life-sciences',
    name: 'Life Sciences',
    icon: FlaskConical,
    challenge: 'Controlled environments to monitor continuously, and an audit trail every change must leave.',
    solution: 'Field-level audit, electronic signatures on critical actions, MFA, password policy and security levels, SMS and voice escalation, and seven-year cloud retention.',
    modules: ['security', 'alarms', 'analytics'],
  },
  {
    slug: 'water',
    name: 'Water & Utilities',
    icon: Droplets,
    challenge: 'Remote pump stations and tanks with no staff on site and alarms that must reach someone at 2 a.m.',
    solution: 'Modbus RTU over serial, voice-call alarm escalation to whoever is on call, fleet health monitoring and remote viewing.',
    modules: ['connectivity', 'alarms', 'visualization'],
  },
  {
    slug: 'facilities',
    name: 'Facilities & Energy',
    icon: Building2,
    challenge: 'HVAC, compressed air and metering on separate building systems with no shared view of energy use.',
    solution: 'Modbus meters and sensors on one dashboard, energy reports by shift and alarms on abnormal consumption.',
    modules: ['connectivity', 'visualization', 'analytics'],
  },
];
